import {Component, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {HeaderService} from '../../../core/services/header.service';
import {Title} from '@angular/platform-browser';
import {BaseComponent} from '../../../core/base.component';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {NotificationService} from '../../../notification/notification.service';
import {AuthService} from '../../../core/security/auth.service';
import {RolesEnum} from '../../../core/security/roles.enum';
import {CustomFieldsService} from '../custom-fields.service';
import {MatDialog} from '@angular/material/dialog';
import {MatConfirmDialogComponent} from '../../../dialogs/mat-confirm/mat-confirm-dialog.component';
import {CustomFieldModel} from '../../../api/models/custom-field-model';
import {Subscription} from 'rxjs';
import { MatCard, MatCardContent } from '@angular/material/card';
import { NgIf } from '@angular/common';
import { MatSimpleSelectComponent } from '../../../editors/mat-simple-select/mat-simple-select.component';
import { MatTextInputComponent } from '../../../editors/mat-text-input/mat-text-input.component';
import { MatNumberInputComponent } from '../../../editors/mat-number-input/mat-number-input.component';
import { MatCheckboxInputComponent } from '../../../editors/mat-checkbox-input/mat-checkbox-input.component';
import { FlexModule } from '@angular/flex-layout';
import { MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { Angular2PromiseButtonModule } from 'angular2-promise-buttons';
import { MatIcon } from '@angular/material/icon';

@Component({
    selector: 'app-custom-field-edit',
    templateUrl: './custom-field-edit.component.html',
    styleUrls: ['./custom-field-edit.component.scss'],
    standalone: true,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        MatCard,
        MatCardContent,
        NgIf,
        MatSimpleSelectComponent,
        MatTextInputComponent,
        MatNumberInputComponent,
        MatCheckboxInputComponent,
        FlexModule,
        MatButton,
        MatTooltip,
        Angular2PromiseButtonModule,
        MatIcon,
    ],
})
export class CustomFieldEditComponent extends BaseComponent implements OnInit, OnChanges, OnDestroy {

  form: UntypedFormGroup;
  fieldKeyControl = new UntypedFormControl('', [Validators.required, Validators.pattern('^[a-zA-Z_][0-9a-zA-Z_]*([.][a-zA-Z_][0-9a-zA-Z_]*)*$')]);
  fieldTitleControl = new UntypedFormControl('', [Validators.required]);
  fieldTypeControl = new UntypedFormControl('text', [Validators.required]);
  fieldFormatControl = new UntypedFormControl('', []);
  categoryControl = new UntypedFormControl('0', []);
  requiredControl = new UntypedFormControl(false, []);
  includeInSearchControl = new UntypedFormControl(false, []);
  includeInAnySearchControl = new UntypedFormControl(false, []);
  fieldOrderControl = new UntypedFormControl(0, []);

  submitted: boolean;
  serverError: boolean;
  lastServerError: string;

  editModel: CustomFieldModel = {
    id: 'new',
    category: 0,
    fieldKey: '',
    fieldTitle: '',
    fieldType: 'text',
    fieldFormat: '',
    required: false,
    includeInSearch: false,
    includeInAnySearch: false,
    fieldOrder: 0
  };

  buttonSubmitPromise: Promise<void>;
  private paramsSubscription: Subscription;
  private id: string;

  constructor(route: ActivatedRoute, headerService: HeaderService, titleService: Title, authService: AuthService,
              public dialog: MatDialog,
              public customFieldsService: CustomFieldsService,
              private fb: UntypedFormBuilder,
              private notificationService: NotificationService) {
    super(route, headerService, titleService, authService);

    this.paramsSubscription = this.route.params.subscribe(params => {
      this.id = params['id'];
      console.log(this.id);
    });

    this.editRoles = [RolesEnum.CustomFieldsEdit, RolesEnum.CustomFieldsDelete];
    this.deleteRoles = [RolesEnum.CustomFieldsDelete];

    this.headerService.showBackButton(true);

    this.createForm();
  }

  createForm(): void {
    this.form = this.fb.group({
        'fieldTitle': this.fieldTitleControl,
        'fieldKey': this.fieldKeyControl,
        'category': this.categoryControl,
        'fieldType': this.fieldTypeControl,
        'fieldFormat': this.fieldFormatControl,
        'required': this.requiredControl,
        'includeInSearch': this.includeInSearchControl,
        'includeInAnySearch': this.includeInAnySearchControl,
        'fieldOrder': this.fieldOrderControl,
      }
    );
  }

  async onSubmit() {
    this.serverError = false;
    this.submitted = true;

    if (this.form.valid && this.hasEditAccess) {
      this.buttonSubmitPromise = this.processSubmit();
    } else {
      setTimeout(() => {
        this.submitted = false;
      });
    }
  }

  async processSubmit(): Promise<void> {
    try {
      this.submitted = true;
      const reqData = this.prepareFormModel();
      await this.customFieldsService.saveCustomField(reqData);
      this.notificationService.notifySuccess('Request successfully submitted');
      this.headerService.goBack();
    } catch (e: any) {
      if (e.error?.message) {
        this.lastServerError = e.error.message;
      } else {
        this.lastServerError = '';
      }
      this.serverError = true;
    } finally {
      this.submitted = false;
    }
  }

  prepareFormModel(): CustomFieldModel {
    const formModel = this.form.getRawValue();

    return {
      id: this.editModel.id,
      category: Number(formModel.category || 0),
      fieldKey: formModel.fieldKey?.trim(),
      fieldTitle: formModel.fieldTitle?.trim(),
      fieldType: formModel.fieldType,
      fieldFormat: formModel.fieldFormat || '',
      required: formModel.required || false,
      includeInSearch: formModel.includeInSearch || false,
      includeInAnySearch: formModel.includeInAnySearch || false,
      fieldOrder: formModel.fieldOrder || 0,
    };
  }

  ngOnInit() {
    super.ngOnInit();

    if (this.id !== 'new') {
      this.customFieldsService.getCustomFieldById(this.id).then(value => {
        this.editModel = value;
        this.ngOnChanges();
      });
    }
  }

  ngOnChanges(changes?: SimpleChanges): void {
    this.form.reset({
      category: `${this.editModel.category || 0}`,
      fieldKey: this.editModel.fieldKey,
      fieldTitle: this.editModel.fieldTitle,
      fieldType: this.editModel.fieldType,
      fieldFormat: this.editModel.fieldFormat,
      required: this.editModel.required,
      includeInSearch: this.editModel.includeInSearch,
      includeInAnySearch: this.editModel.includeInAnySearch,
      fieldOrder: this.editModel.fieldOrder,
    });
  }

  onDeleteClick() {
    const dialogRef = this.dialog.open(MatConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Delete Custom Field?',
        text: 'Are you sure you want to delete Custom Field?',
        actionTitle: 'Delete',
      }
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        await this.customFieldsService.deleteCustomField(this.editModel.id);
        this.notificationService.notifySuccess('Custom Field has been deleted');

        this.headerService.goBack();
      }
    });
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.paramsSubscription?.unsubscribe();
    this.headerService.showBackButton(false);
  }
}
